import React, { useMemo, useState } from 'react'
import { ChevronLeft, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useSearchParams } from 'react-router'
import { Menu, MenuItem, MenuSeparator } from '../ui'
import { useWatchlist } from '../../contexts/WatchlistContext'
import { useToast } from '../../contexts/ToastContext'
import type { WatchlistList } from '../../services/watchlist'
import { flattenWatchlist } from '../../services/watchlist'
import WatchlistCreateListInline from './WatchlistCreateListInline'
import {
  parseWatchlistListFilter,
  serialiseWatchlistListFilter,
  type WatchlistListFilterId,
} from '../../utils/watchlistListFilter'

/**
 * List filter navigation in the account sidebar drill-down (/account/watchlist).
 */
const WatchlistListNav: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { data, lists, createList, renameList, deleteList } = useWatchlist()
  const { showToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const filter = parseWatchlistListFilter(searchParams.get('list'))
  const uniqueItems = useMemo(() => flattenWatchlist(data), [data])
  const uncategorizedCount = data.uncategorized.length
  const totalCount = uniqueItems.length

  const setFilter = (id: WatchlistListFilterId) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        const v = serialiseWatchlistListFilter(id)
        if (v === null) params.delete('list')
        else params.set('list', v)
        return params
      },
      { replace: true }
    )
  }

  const [renamingId, setRenamingId] = useState<number | null>(null)

  async function handleCreate(name: string) {
    try {
      const list = await createList(name)
      showToast('Liste oprettet.')
      return list
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
      throw err
    }
  }

  async function handleDelete(list: WatchlistList) {
    if (
      !window.confirm(
        `Slet listen «${list.name}»? Entiteterne bliver uden liste, ikke slettet.`
      )
    ) {
      return
    }
    try {
      await deleteList(list.id)
      showToast('Liste slettet.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  return (
    <nav aria-label="Watchlist-lister" className="w-full">
      <button
        type="button"
        onClick={onBack}
        className="group inline-flex items-center gap-1 text-xs uppercase tracking-wide text-ink-500 hover:text-ink transition-colors mb-4"
      >
        <ChevronLeft
          className="h-3.5 w-3.5 opacity-70 transition-transform duration-200 group-hover:-translate-x-0.5"
          aria-hidden
        />
        Indstillinger
      </button>

      <ul className="space-y-0.5">
        <li>
          <FilterButton
            active={filter === 'all'}
            count={totalCount}
            onClick={() => setFilter('all')}
          >
            Alle
          </FilterButton>
        </li>
        <li>
          <FilterButton
            active={filter === 'uncategorized'}
            count={uncategorizedCount}
            onClick={() => setFilter('uncategorized')}
          >
            Uden liste
          </FilterButton>
        </li>
        {lists.length > 0 && (
          <li aria-hidden className="my-2 border-t border-rule" />
        )}
        {lists.map((l) => (
          <li key={l.id}>
            {renamingId === l.id ? (
              <RenameForm
                initial={l.name}
                onCancel={() => setRenamingId(null)}
                onSave={async (next) => {
                  if (next === l.name) {
                    setRenamingId(null)
                    return
                  }
                  try {
                    await renameList(l.id, next)
                    setRenamingId(null)
                    showToast('Liste omdøbt.')
                  } catch (err: unknown) {
                    showToast(err instanceof Error ? err.message : 'Fejl', {
                      tone: 'signal',
                    })
                  }
                }}
              />
            ) : (
              <FilterButton
                active={filter === `l-${l.id}`}
                count={l.items.length}
                onClick={() => setFilter(`l-${l.id}`)}
                title={l.name}
                actions={
                  <Menu
                    align="right"
                    trigger={<MoreHorizontal className="h-3.5 w-3.5" aria-hidden />}
                    triggerAriaLabel={`Handlinger for ${l.name}`}
                    triggerClassName="p-1"
                  >
                    {(close) => (
                      <>
                        <MenuItem
                          icon={<Pencil className="h-3.5 w-3.5" />}
                          onClick={() => {
                            close()
                            setRenamingId(l.id)
                          }}
                        >
                          Omdøb
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem
                          danger
                          icon={<Trash2 className="h-3.5 w-3.5" />}
                          onClick={() => {
                            close()
                            void handleDelete(l)
                          }}
                        >
                          Slet liste
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                }
              >
                {l.name}
              </FilterButton>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-4 border-t border-rule pt-4">
        <WatchlistCreateListInline
          inputId="new-list"
          onCreate={handleCreate}
        />
      </div>
    </nav>
  )
}

const FilterButton: React.FC<{
  active: boolean
  count: number
  onClick: () => void
  children: React.ReactNode
  actions?: React.ReactNode
  title?: string
}> = ({ active, count, onClick, children, actions, title }) => (
  <div
    className={`group flex items-center rounded-sm ${
      active ? 'bg-paper-200' : 'hover:bg-paper-200/50'
    }`}
  >
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex min-w-0 flex-1 items-center pl-2.5 py-1.5 pr-0 text-sm tracking-wide transition-colors ${
        active ? 'text-ink font-semibold' : 'text-ink-500 hover:text-ink'
      }`}
    >
      <span className="min-w-0 flex-1 truncate text-left" title={title}>
        {children}
      </span>
      <span className="w-7 shrink-0 text-right font-mono text-xs text-ink-400 tabular-nums">
        {count}
      </span>
    </button>
    <div className="w-7 shrink-0 flex items-center justify-end pr-1 [&_button[aria-haspopup]]:opacity-0 [&_button[aria-haspopup]]:transition-opacity group-hover:[&_button[aria-haspopup]]:opacity-100 group-focus-within:[&_button[aria-haspopup]]:opacity-100">
      {actions}
    </div>
  </div>
)

function RenameForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: string
  onSave: (next: string) => Promise<void>
  onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const [busy, setBusy] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const next = value.trim()
    if (!next) return
    setBusy(true)
    try {
      await onSave(next)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-1 px-2.5 py-1">
      <input
        type="text"
        value={value}
        autoFocus
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (value.trim() === initial) onCancel()
        }}
        aria-label="Listenavn"
        className="flex-1 min-w-0 border-b border-ink bg-paper text-sm text-ink py-1 focus:outline-none"
      />
      <button
        type="submit"
        disabled={busy || !value.trim()}
        className="text-xs text-signal hover:text-signal-700 font-medium px-1"
      >
        Gem
      </button>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Annuller omdøbning"
        className="text-ink-500 hover:text-ink p-1"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </form>
  )
}

export default WatchlistListNav
